// Reports and drawings, recognised by what the document says and what its
// pages are (CN3, Peter 2026-10-13: "file names shouldnt dictate anything.
// only the contents.").
//
// Before CN3 a file NAME decided both classes before any text was read: a name
// holding "engineer", "structural", "compliance" or "asbestos" made a document
// a report, and one holding "plan", "drawing", "engineering" or "survey" made
// it a drawing and paid for full-page vision with the drawing prompt. So a
// quote for structural roofing repairs was a report, a reply about "Strata
// Plan 1234" was a drawing, and a 65-page engineering specification was read
// page by page as a drawing. This module reads the text and the page sizes.
// A file name, an extension and a folder are never inputs.
//
// detectReportByContent: a report names itself in its title ("Site Inspection
// Report", "Structural Building Report", "Asbestos Register", "Design
// Certificate") or has a report's sections (introduction, findings,
// recommendations, prepared by, numbered photographs). It is never a request
// for a quote, a quote or its cover note, the estimator's priced notes, an
// invoice or a message that only names a report.
//
// detectPlanByContent: a drawing has a title block and drawing marks (drawing
// number, scale 1:N, sheet N of M, revision table, drawn and checked by,
// plan, section and elevation views, levels and grid lines) and usually a
// large or landscape page with little text. A page with no text layer is read
// by its page size alone, so a scanned A4 sketch carries no drawing signal
// and is read by the ordinary page reader. It is never a request for a quote,
// a quote, the estimator's notes or a report.
//
// Pure and synchronous. Reads the first 8,000 characters, the same window as
// the document classifier.

import { detectEstimatorPricedNotes } from './estimator-notes-detector';
import {
    countMatches,
    detectRfqByContent,
    earliestMatch,
    INVOICE_RES,
    normaliseDetectorText,
    PLAN_MARKER_RES,
    REPORT_SECTION_RES,
    REPORT_TITLE_AT_START_CHARS,
    REPORT_TITLE_RES,
    TITLE_CHARS,
    type RfqContentResult,
} from './rfq-content-detector';

// ─── Reports ────────────────────────────────────────────────────────────────

export type ReportContentRejection = 'request' | 'estimator-notes' | 'quote' | 'invoice' | 'message';

export interface ReportContentResult {
    isReport: boolean;
    /** The report's own title, when it has one. */
    title: string | null;
    /** The report sections found in the text. */
    sections: string[];
    /** Why the text is not a report, when a rejection decided it. */
    rejectedAs: ReportContentRejection | null;
    evidence: string[];
}

// Titles a specialist or the office gives a report, on top of the titles the
// request detector already reads (REPORT_TITLE_RES). A certificate an engineer
// or an inspector signs is read as a report: it is technical evidence, not a
// price and not a request.
const EXTRA_REPORT_TITLE_RES: RegExp[] = [
    /\b(?:design|engineer(?:'?s|ing)?|structural|inspection)\s+certificate\b/i,
    /\b(?:dilapidation|condition|defects?)\s+survey\b/i,
    /\bbuilding\s+condition\s+assessment\b/i,
];
// A short title line at the top that ends with the word "report"
// ("Building Facade Remediation Report", "Report", "SITE REPORT - Level 3").
// Up to 90 characters, not a sentence and not a reference to another
// document ("Re: engineer's report", "attached report").
const REPORT_TITLE_LINE_RE = /^(?!\s*(?:re|fw|fwd|subject)\s*:)(?![^\n]*\b(?:attached|enclosed|refer|per|see|as\s+per|following)\b)[^\n.?!]{0,80}\breport\b(?:\s*(?:[-–:#]\s*)?(?:no\.?|number|rev(?:ision)?\.?)?\s*[\w./-]{0,12})?\s*$/i;
// How many non-empty lines are read for a title line: the top of the text, or
// every line within reach of a builder's letterhead.
const TITLE_LINES = 6;
const LETTERHEAD_TITLE_LINES = Number.POSITIVE_INFINITY;
// How far a builder's letterhead can push a report's title down.
const LETTERHEAD_TITLE_CHARS = 700;

// A title is written as one: it starts with a capital ("Site Report", "TEST
// REPORT") and is not part of a sentence. The same words in a sentence
// ("Reviewed the engineers report", "Engineers report matched the photos",
// "for your Asbestos Survey") name another document.
function inSentence(zone: string, index: number, length: number): boolean {
    const lineStart = zone.lastIndexOf('\n', index - 1) + 1;
    const nextBreak = zone.indexOf('\n', index + length);
    const before = zone.slice(lineStart, index);
    const after = zone.slice(index + length, nextBreak < 0 ? zone.length : nextBreak);
    return /\b[a-z]{2,}\b/.test(before) || /^\s+[a-z]{2,}\b/.test(after);
}

function capitalisedTitle(zone: string, patterns: RegExp[]): { text: string; index: number } | null {
    let best: { text: string; index: number } | null = null;
    for (const pattern of patterns) {
        const global = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`);
        for (const match of zone.matchAll(global)) {
            if (match.index === undefined || !/^[A-Z]/.test(match[0])) continue;
            if (inSentence(zone, match.index, match[0].length)) continue;
            if (!best || match.index < best.index) best = { text: match[0].replace(/\s+/g, ' ').trim(), index: match.index };
            break;
        }
    }
    return best;
}

function reportTitle(text: string, zoneChars = TITLE_CHARS, titleLines = TITLE_LINES): { text: string; index: number } | null {
    const zone = text.slice(0, zoneChars).replace(/^[ \t>]*subject\s*:[^\n]*$/gim, '');
    const known = capitalisedTitle(zone, [...REPORT_TITLE_RES, ...EXTRA_REPORT_TITLE_RES]);
    let lineMatch: { text: string; index: number } | null = null;
    let offset = 0;
    const lines = zone.split('\n');
    for (let i = 0, seen = 0; i < lines.length && seen < titleLines; i++) {
        const line = lines[i] ?? '';
        const trimmed = line.trim();
        if (trimmed) {
            seen++;
            if (trimmed.length <= 90 && REPORT_TITLE_LINE_RE.test(trimmed) && /\b(?:Report|REPORT)\b/.test(trimmed)) {
                lineMatch = { text: trimmed, index: offset + line.indexOf(trimmed) };
                break;
            }
        }
        offset += line.length + 1;
    }
    if (known && lineMatch) return known.index <= lineMatch.index ? known : lineMatch;
    return known || lineMatch;
}

// An email by its headers (the importer writes a decoded email as its header
// lines, then the body). A sign-off alone does not make a message: a report
// or a certificate ends with "Kind regards" too. "Date:" and "To:" alone are
// a letter's or a report's address block, so a From, Sent or Subject line is
// needed as well.
const MESSAGE_HEADER_LINE_RE = /^[ \t>]*(?:from|to|sent|date|subject|cc)\s*:/gim;
const SENDER_HEADER_LINE_RE = /^[ \t>]*(?:from|sent|subject)\s*:/im;
const LEADING_MESSAGE_HEADER_RE = /^\s*(?:subject|from)\s*:/i;

function messageHeaders(text: string): boolean {
    const head = text.slice(0, 2000);
    return LEADING_MESSAGE_HEADER_RE.test(text)
        || ((head.match(MESSAGE_HEADER_LINE_RE) || []).length >= 2 && SENDER_HEADER_LINE_RE.test(head));
}

function notReport(rejectedAs: ReportContentRejection | null, evidence: string[], sections: string[] = []): ReportContentResult {
    return { isReport: false, title: null, sections, rejectedAs, evidence };
}

/**
 * Whether a text is a report, read from the text alone. Never reads a file
 * name, an extension or a folder. Pass the request detector's result when the
 * caller already has it, so the text is not read twice.
 */
export function detectReportByContent(input: unknown, request?: RfqContentResult): ReportContentResult {
    const raw = typeof input === 'string' ? input : '';
    // PDF text keeps separate items on one line with a run of spaces between
    // them; each item is read as its own line, so a title is not taken for
    // part of the sentence printed beside it.
    const text = normaliseDetectorText(raw).replace(/[ \t]{2,}/g, '\n');
    if (text.replace(/\s+/g, '').length < 20) return notReport(null, ['too little text to read']);

    const notes = detectEstimatorPricedNotes(raw);
    if (notes.isEstimatorNotes) {
        return notReport('estimator-notes', [`it reads as the estimator's own priced notes (${notes.evidence.join('; ')})`]);
    }
    const rfq = request || detectRfqByContent(raw);
    if (rfq.isRfq) return notReport('request', ['it reads as the client\'s request for a quote']);
    if (rfq.rejectedAs === 'quote') return notReport('quote', rfq.evidence);
    if (rfq.rejectedAs === 'inspection-request') return notReport('request', rfq.evidence);
    if (rfq.rejectedAs === 'reply') return notReport('message', rfq.evidence);

    const sections = countMatches(text, REPORT_SECTION_RES);
    const title = reportTitle(text);
    const head = text.slice(0, 700);
    const invoice = earliestMatch(head, INVOICE_RES);
    if (invoice && !title) return notReport('invoice', [`it reads as an invoice or statement ("${invoice.text}")`], sections);

    // A message names the report it carries ("engineer's report attached"); it
    // is a report only when the report itself is in the message.
    if (messageHeaders(text)) {
        if (sections.length >= 4) {
            return { isReport: true, title: title?.text || null, sections, rejectedAs: null, evidence: [`the message carries a report (sections: ${sections.slice(0, 4).join(', ')})`] };
        }
        return notReport('message', ['it is a message, which only names a report'], sections);
    }
    // A builder's letterhead (licence numbers, member logos, a list of
    // services) pushes the title down: a title in the title zone counts with
    // one section, and one further down the head with two.
    const titledAtStart = Boolean(title) && title!.index < REPORT_TITLE_AT_START_CHARS;
    const lowerTitle = title ? null : reportTitle(text, LETTERHEAD_TITLE_CHARS, LETTERHEAD_TITLE_LINES);
    if (titledAtStart || (title && sections.length >= 1) || (lowerTitle && sections.length >= 2) || sections.length >= 4) {
        const found = title || lowerTitle;
        const why = found ? `titled "${found.text}"` : 'no title';
        return { isReport: true, title: found?.text || null, sections, rejectedAs: null, evidence: [`it reads as a report (${why}; sections: ${sections.slice(0, 4).join(', ') || 'none'})`] };
    }
    return notReport(null, [title ? `titled "${title.text}" but no report section` : 'no report title and too few report sections'], sections);
}

// ─── Drawings ───────────────────────────────────────────────────────────────

export type PlanContentRejection = 'request' | 'estimator-notes' | 'quote' | 'report' | 'message';

export interface PageGeometryLike {
    width?: unknown;
    height?: unknown;
}

export interface PlanContentInput {
    /** The text read from the document (its text layer, or text a reader already produced). */
    text?: unknown;
    /** The length of the whole text, when only a sample is passed. */
    textLength?: number | null;
    /** Page sizes in PDF units, first page first. */
    pages?: PageGeometryLike[] | null;
}

export interface PlanContentResult {
    isPlan: boolean;
    /** 0 to 100. A drawing scores 50 or more. */
    confidence: number;
    /** Short signal names: drawing-marks, text-sparse, large-page, landscape-page. */
    signals: string[];
    /** The drawing marks found in the text. */
    marks: string[];
    rejectedAs: PlanContentRejection | null;
    evidence: string[];
}

// Marks of a drawing and its title block. Each counts once.
const DRAWING_MARK_RES: RegExp[] = [
    ...PLAN_MARKER_RES,
    /\b(?:dwg|drg)\.?\s*(?:no\b\.?|number|#)/i,
    /\b(?:not\s+to\s+scale|n\.t\.s\.?(?![a-z]))/i,
    /\b1\s*:\s*\d{2,4}\s*@\s*A[0-4]\b/i,
    /\brev(?:ision)?\s+date\s+(?:description|amendment|details)\b|\b(?:amendment|revision)\s+(?:schedule|history|table)\b/i,
    /\brevision\s+[A-Z]\d\b/i,
    /\bsection\s+[A-Z]\s*[-–]\s*[A-Z]\b/i,
    // Levels: written in capitals on a drawing, so these are case-sensitive.
    /\b(?:FFL|SSL|TOW)\b|\bFL\s*\+|\bRL\s*[:=]?\s*\+?\d/,
    /\bdatum\b/i,
    /\bgrid\s+line\b|\bGRID\s*[A-J]\d\b/i,
    /\b(?:project|true)\s+north\b/i,
    /\b(?:roof|ground\s+floor|first\s+floor|level\s+\d+|locality|location|demolition|setout|set\s+out|framing|footing|slab)\s+plan\b/i,
    /\belevation\s+(?:north|south|east|west|[NSEW])\b|\b(?:north|south|east|west)(?:ern)?\s+elevation\b/i,
    /\bdesign\s+plan\s+record\b|\bregulated\s+design\s+record\b/i,
    /\bissued?\s+for\s+(?:construction|approval|information|coordination|da|cc|cdc|review)\b/i,
    /\blengths\s+are\s+in\s+(?:metres|millimetres)\b|\b(?:all\s+)?dimensions\s+(?:are\s+)?in\s+(?:mm|millimetres)\b|\breduction\s+ratio\b/i,
];
const PLAN_READ_CHARS = 8000;
const SPARSE_TEXT_CHARS = 600;
const LARGE_PAGE_UNITS = 1500;   // longer edge, PDF units: larger than A3
const LANDSCAPE_ASPECT = 1.3;
export const PLAN_CONFIDENCE_MIN = 50;
const REJECTION_WORDS: Record<PlanContentRejection, string> = {
    request: 'the client\'s request for a quote',
    'estimator-notes': 'the estimator\'s priced notes',
    quote: 'a quote',
    report: 'a report',
    message: 'a message',
};

function pageSize(page: PageGeometryLike | null | undefined): { width: number; height: number } | null {
    const width = Number(page?.width);
    const height = Number(page?.height);
    return Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0 ? { width, height } : null;
}

/**
 * Whether a document is a drawing, read from its text and its page sizes.
 * Never reads a file name, an extension or a folder.
 */
export function detectPlanByContent({ text, textLength = null, pages = null }: PlanContentInput = {}): PlanContentResult {
    const raw = typeof text === 'string' ? text : '';
    const sample = raw.slice(0, PLAN_READ_CHARS);
    const length = Number.isFinite(Number(textLength)) && textLength !== null ? Number(textLength) : raw.length;
    const signals: string[] = [];
    const evidence: string[] = [];
    let confidence = 0;

    const marks = countMatches(normaliseDetectorText(sample), DRAWING_MARK_RES);
    if (marks.length > 0) {
        signals.push('drawing-marks');
        confidence += marks.length >= 3 ? 60 : marks.length === 2 ? 45 : 35;
        evidence.push(`drawing marks: ${marks.slice(0, 4).join(', ')}`);
    }
    if (length >= 0 && length < SPARSE_TEXT_CHARS) {
        signals.push('text-sparse');
        confidence += 15;
    }
    const first = pageSize(Array.isArray(pages) ? pages[0] : null);
    if (first) {
        const longEdge = Math.max(first.width, first.height);
        const aspect = longEdge / Math.min(first.width, first.height);
        if (longEdge > LARGE_PAGE_UNITS) {
            signals.push('large-page');
            confidence += 25;
        }
        if (aspect > LANDSCAPE_ASPECT && first.width >= first.height) {
            signals.push('landscape-page');
            confidence += 10;
        }
        evidence.push(`first page ${Math.round(first.width)} x ${Math.round(first.height)}`);
    }
    confidence = Math.min(100, confidence);

    // What a drawing is not, read only when there is text to read.
    let rejectedAs: PlanContentRejection | null = null;
    if (confidence >= PLAN_CONFIDENCE_MIN && raw.replace(/\s+/g, '').length >= 20) {
        const request = detectRfqByContent(raw);
        if (request.isRfq) rejectedAs = 'request';
        else if (request.rejectedAs === 'estimator-notes') rejectedAs = 'estimator-notes';
        else if (request.rejectedAs === 'quote') rejectedAs = 'quote';
        else if (messageHeaders(normaliseDetectorText(raw))) rejectedAs = 'message';
        else if (detectReportByContent(raw, request).isReport) rejectedAs = 'report';
        if (rejectedAs) evidence.push(`not a drawing: the text reads as ${REJECTION_WORDS[rejectedAs]}`);
    }
    return { isPlan: confidence >= PLAN_CONFIDENCE_MIN && !rejectedAs, confidence, signals, marks, rejectedAs, evidence };
}

// ─── Shared with the scope of works detector (CN4) ──────────────────────────
// Exported as they are, so a scope of works title and a message are read the
// same way a report title and a message are. Exporting changes nothing above.
export { capitalisedTitle, messageHeaders };
